import { Grid, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { TAutomationRuleEncMessageForm } from "./automationRuleEncMessageSchema";
import {
  AutoCompleteMulti,
  DateInput,
  FormInput,
  ISelectType,
  SwitchInput,
  useFetch,
  useFormContext,
} from "@4uhub/lib4uhub";
import { useTranslation } from "react-i18next";
import { SelectEstablishment } from "./SelectEstablishment";
import { sectorEmr } from "../../../../../../../services/sectorEmr.service";
import { domainValueList } from "../../../../../../../services/domainValue.service";
import { internalProcClassification } from "../../../../../../../services/internalProcClassification.service";
import { useAutomationRules } from "../../../../../../../store/contexts/AutomationRulesContext";
import { IndividualIdentifierField } from "./IndividualIdentifierField";

const translationPath = "chatBot.page.automations.automationRuleEncMessage.";

export const If = memo(({ idMessage }: { idMessage?: string }) => {
  const { t } = useTranslation();

  const [
    ticketAutomationRuleEncMessageSectors,
    setTicketAutomationRuleEncMessageSectors,
  ] = useState<ISelectType[]>([]);

  const [
    ticketAutomationRuleEncMessageEncounterTypes,
    setTicketAutomationRuleEncMessageEncounterTypes,
  ] = useState<ISelectType[]>([]);

  const [
    ticketAutomationRuleEncMessageInProcClassifics,
    setTicketAutomationRuleEncMessageInProcClassifics,
  ] = useState<ISelectType[]>([]);

  const [
    ticketAutomationRuleEncMessageMedProcTypes,
    setTicketAutomationRuleEncMessageMedProcTypes,
  ] = useState<ISelectType[]>([]);

  const { sendRequest: sectorEmrRequest } = useFetch(sectorEmr);

  const { sendRequest: domainValueListRequest } = useFetch(domainValueList);

  const { sendRequest: internalProcClassificationRequest } = useFetch(
    internalProcClassification,
  );

  const { formState } = useFormContext<TAutomationRuleEncMessageForm>();

  const { automation } = useAutomationRules();

  const automationCode = useMemo(
    () => (automation && automation.code ? automation.code : null),
    [automation],
  );

  const exceptionError = !!formState?.errors?.exception;

  const fetchTicketAutomationRuleEncMessageSectors = useCallback(async () => {
    const { data, success } = await sectorEmrRequest(null);
    if (data && success) {
      setTicketAutomationRuleEncMessageSectors(
        data.map((d) => ({
          id: d.identifier.toString(),
          name: d.name,
        })),
      );
    }
  }, [sectorEmrRequest]);

  useEffect(() => {
    fetchTicketAutomationRuleEncMessageSectors();
  }, [fetchTicketAutomationRuleEncMessageSectors]);

  const fetchTicketAutomationRuleEncMessageEncounterTypes =
    useCallback(async () => {
      const { data, success } = await domainValueListRequest({
        Identifier: "12",
      });
      if (data && success) {
        setTicketAutomationRuleEncMessageEncounterTypes(
          data.map((d) => ({
            id: d.identifierValue.toString(),
            name: d.domainValueName,
          })),
        );
      }
    }, [domainValueListRequest]);

  useEffect(() => {
    fetchTicketAutomationRuleEncMessageEncounterTypes();
  }, [fetchTicketAutomationRuleEncMessageEncounterTypes]);

  const fetchTicketAutomationRuleEncMessageInProcClassifics =
    useCallback(async () => {
      const { data, success } = await internalProcClassificationRequest(null);
      if (data && success) {
        setTicketAutomationRuleEncMessageInProcClassifics(
          data.map((d) => ({
            id: d.identifier.toString(),
            name: d.name,
          })),
        );
      }
    }, [domainValueListRequest]);

  useEffect(() => {
    fetchTicketAutomationRuleEncMessageInProcClassifics();
  }, [fetchTicketAutomationRuleEncMessageInProcClassifics]);

  const fetchTicketAutomationRuleEncMessageMedProcTypesParams =
    useCallback(async () => {
      const { data, success } = await domainValueListRequest({
        Identifier: "95",
      });
      if (data && success) {
        setTicketAutomationRuleEncMessageMedProcTypes(
          data.map((d) => ({
            id: d.identifierValue.toString(),
            name: d.domainValueName,
          })),
        );
      }
    }, [domainValueListRequest]);

  useEffect(() => {
    fetchTicketAutomationRuleEncMessageMedProcTypesParams();
  }, [fetchTicketAutomationRuleEncMessageMedProcTypesParams]);

  return (
    <Grid
      container
      spacing={2}
      sx={(t) => ({
        pr: 3,
        pl: 1,
        py: 3,
        border: `1px solid ${
          exceptionError ? t.palette.error.main : t.palette.divider
        }`,
        borderRadius: 2,
      })}
    >
      <Grid item sm={12}>
        <Typography fontWeight="bold">{t(translationPath + "if")}</Typography>
      </Grid>

      <Grid item sm={12} textAlign={"end"}>
        <SwitchInput name="enable" label={t(translationPath + "enable")} />
        <SwitchInput
          name="exception"
          label={t(translationPath + "exception")}
          disabled={idMessage ? true : false}
        />
      </Grid>

      <Grid item xs={12} sm={9}>
        <FormInput
          size="small"
          name="name"
          fullWidth
          label={t(translationPath + "name") || "Name"}
        />
      </Grid>

      <Grid item xs={12} sm={3}>
        <FormInput
          size="small"
          name="priority"
          fullWidth
          label={t(translationPath + "priority") || "Priority"}
        />
      </Grid>

      <Grid item xs={12} sm={4.5}>
        <DateInput
          name="initialDate"
          label={t(translationPath + "initial_date")}
        />
      </Grid>

      <Grid item xs={12} sm={4.5}>
        <DateInput name="endDate" label={t(translationPath + "end_date")} />
      </Grid>

      <Grid item xs={12} sm={3}>
        <FormInput
          type="number"
          size="small"
          name="qtyMessageSendingAttempts"
          fullWidth
          label={
            t(translationPath + "qtyMessageSendingAttempts") ||
            "qtyMessageSendingAttempts"
          }
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <AutoCompleteMulti
          name="ticketAutomationRuleEncMessageSectors"
          size="small"
          label={t(translationPath + "ticketAutomationRuleEncMessageSectors")}
          options={ticketAutomationRuleEncMessageSectors}
          getOptionLabel={({ name }) => name}
          keepOpenOnSelect
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <AutoCompleteMulti
          name="ticketAutomationRuleEncMessagePrescriptionSectors"
          size="small"
          label={t(
            translationPath +
              "ticketAutomationRuleEncMessagePrescriptionSectors",
          )}
          options={ticketAutomationRuleEncMessageSectors}
          getOptionLabel={({ name }) => name}
          keepOpenOnSelect
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <AutoCompleteMulti
          name="ticketAutomationRuleEncMessageEncounterTypes"
          size="small"
          label={t(
            translationPath + "ticketAutomationRuleEncMessageEncounterTypes",
          )}
          options={ticketAutomationRuleEncMessageEncounterTypes}
          getOptionLabel={({ name }) => name}
          keepOpenOnSelect
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <AutoCompleteMulti
          name="ticketAutomationRuleEncMessageInProcClassifics"
          size="small"
          label={t(
            translationPath + "ticketAutomationRuleEncMessageInProcClassifics",
          )}
          options={ticketAutomationRuleEncMessageInProcClassifics}
          getOptionLabel={({ name }) => name}
          keepOpenOnSelect
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <AutoCompleteMulti
          name="ticketAutomationRuleEncMessageMedProcTypes"
          size="small"
          label={t(
            translationPath + "ticketAutomationRuleEncMessageMedProcTypes",
          )}
          options={ticketAutomationRuleEncMessageMedProcTypes}
          getOptionLabel={({ name }) => name}
          keepOpenOnSelect
        />
      </Grid>

      <SelectEstablishment automationCode={String(automationCode)} />
      <IndividualIdentifierField />
    </Grid>
  );
});
